import React, { useState } from "react";
import { Dialog } from '@headlessui/react';
import { useTheme } from '../../CommonComponents/ThemeProvider';

export default function EditStockModal({
    isOpen,
    onClose,
    inventory,
    onSave, // backward-compatible: Stocks Available save
    onSaveAvailable,
    onSaveReleased,
    canEditReleased = false,
    branchLabel = "",
}) {
    const [activeTab, setActiveTab] = useState("available");
    const [availableQuantity, setAvailableQuantity] = useState(inventory ? Number(inventory.quantity) || 0 : 0);
    const [releasedQuantity, setReleasedQuantity] = useState(inventory ? Number(inventory.released_quantity) || 0 : 0);
    const { currentTheme } = useTheme();
    const isRawMaterial = Boolean(inventory?.raw_material_id);
    const step = isRawMaterial ? "0.01" : "1";
    const unitLabel = inventory?.unit || inventory?.unit_type || "units";
    const canShowReleasedTab = canEditReleased && typeof onSaveReleased === "function";

    React.useEffect(() => {
        if (isOpen && inventory) {
            setAvailableQuantity(Number(inventory.quantity) || 0);
            setReleasedQuantity(Number(inventory.released_quantity) || 0);
            setActiveTab("available");
        }
    }, [isOpen, inventory]);

    if (!isOpen) return null;

    const handleSave = () => {
        if (activeTab === "released" && canShowReleasedTab) {
            onSaveReleased(releasedQuantity);
            return;
        }
        if (typeof onSaveAvailable === "function") {
            onSaveAvailable(availableQuantity);
            return;
        }
        if (typeof onSave === "function") {
            onSave(availableQuantity);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center"
        >
            <div className="w-full max-w-sm px-4">
                <Dialog.Panel className="bg-white rounded-xl shadow-xl p-6 md:p-7 text-center space-y-5">
                    <Dialog.Title className="text-lg font-semibold text-gray-800 mb-2">
                        Adjust Stock
                    </Dialog.Title>
                    {canShowReleasedTab && (
                        <div className="flex gap-2 justify-center">
                            <button
                                type="button"
                                onClick={() => setActiveTab("available")}
                                className={`px-3 py-1 rounded text-sm font-medium border ${activeTab === "available"
                                    ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                    }`}
                            >
                                Stocks Available
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab("released")}
                                className={`px-3 py-1 rounded text-sm font-medium border ${activeTab === "released"
                                    ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                    }`}
                            >
                                Stocks Released
                            </button>
                        </div>
                    )}
                    <div className="mb-4 text-left">
                        <label className="block text-sm font-medium mb-1">
                            {activeTab === "released" ? "Stocks Released" : "Stocks Available"}
                        </label>
                        <input
                            type="number"
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none text-lg text-center"
                            value={activeTab === "released" ? releasedQuantity : availableQuantity}
                            min={0}
                            step={step}
                            inputMode={isRawMaterial ? "decimal" : "numeric"}
                            onChange={e => {
                                const value = Number(e.target.value);
                                if (activeTab === "released") {
                                    setReleasedQuantity(value);
                                } else {
                                    setAvailableQuantity(value);
                                }
                            }}
                            placeholder="Enter new stock quantity"
                        />
                        {activeTab === "released" && canShowReleasedTab && (
                            <p className="mt-1 text-xs text-gray-500">
                                Branch: {branchLabel || "Selected Branch"}
                            </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">Unit: {unitLabel}</p>
                    </div>
                    <div className="flex justify-center gap-3 pt-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-md text-sm font-medium transition bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none active:outline-none"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 rounded-md text-sm font-medium transition focus:outline-none active:outline-none"
                            style={{ background: currentTheme.color, color: '#fff' }}
                        >
                            Save
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
